import { useEffect, useRef, useState, useMemo } from "react";
import { VirtuosoHandle } from "react-virtuoso";
import {
  GroupedResult,
  GroupingConfig,
  ScrollManagerConfig,
  RenderContext,
} from "./types";
import { countNewAtTop } from "./countNewAtTop";

interface UseVirtuosoWrapperStateProps<T> {
  data: T[];
  grouping?: GroupingConfig<T>;
  scrollManager?: ScrollManagerConfig<T>;
  isLoadingMore?: boolean;
  /**
   * The reader is mid-something in this list: a row they opened is still
   * open, and they haven't jumped back to the newest since. Holds the scroll
   * position on prepend even at the top of the list, where new items would
   * otherwise push what they are reading down the viewport.
   */
  isEngaged?: boolean;
}

interface UseVirtuosoWrapperStateReturn<T> {
  /** Ref to attach to Virtuoso component */
  virtuosoRef: React.RefObject<VirtuosoHandle>;

  /** Current first item index (for scroll preservation) */
  firstItemIndex: number;

  /** Whether user is at the top of the list */
  atListTop: boolean;

  /** Callback for Virtuoso's atTopStateChange */
  handleAtTopChange: (atTop: boolean) => void;

  /** Whether to show "new items available" notification */
  showUpdateNotification: boolean;

  /** Jump to the top (instantly) and treat the list as being there */
  scrollToTop: () => void;

  /** The grouped result (or flat result if no grouping) */
  groupedData: GroupedResult<T>;

  /** Whether this is a grouped list */
  isGrouped: boolean;

  /** Helper function to get render context for an item */
  getRenderContext: (index: number) => RenderContext;
}

/**
 * Internal hook for managing grouped virtual list state
 * Handles scroll preservation, new items notification, and grouping logic
 */
export function useVirtuosoWrapperState<T>({
  data,
  grouping,
  scrollManager,
  isLoadingMore = false,
  isEngaged = false,
}: UseVirtuosoWrapperStateProps<T>): UseVirtuosoWrapperStateReturn<T> {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Extract scroll config values to avoid creating new objects every render
  const scrollEnabled = scrollManager?.enabled ?? false;
  // Every held arrival lowers firstItemIndex for good, and holds keep working
  // however long a feed runs, so a busy list open for days can spend thousands.
  // Virtuoso errors below zero and says to start "very high".
  const scrollStartingIndex = scrollManager?.startingIndex ?? 10_000_000;
  const compareFirstItem = useMemo(
    () =>
      scrollManager?.compareFirstItem ??
      ((a: T | undefined, b: T | undefined) => a === b),
    [scrollManager?.compareFirstItem]
  );

  // IMPORTANT: Only use custom firstItemIndex if scrollManager is explicitly enabled
  // Initialize with the correct value based on initial props
  // The useState callback ensures correct initialization without race conditions
  const [firstItemIndex, setFirstItemIndex] = useState(() => {
    return scrollEnabled ? scrollStartingIndex : 0;
  });

  const [atListTop, setAtListTop] = useState(true);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  // Determine if this is a grouped list
  const isGrouped = !!grouping;

  // Apply grouping if configured
  const groupedData = useMemo<GroupedResult<T>>(() => {
    if (grouping) {
      const result = grouping.groupBy(data);

      // Validate the grouped result
      const totalCount = result.groupCounts.reduce(
        (sum, count) => sum + count,
        0
      );
      if (totalCount !== result.items.length) {
        console.error(
          "useVirtuosoWrapperState: Mismatch between groupCounts total and items length:",
          totalCount,
          result.items.length
        );
      }

      return result;
    }

    // No grouping - return flat structure
    return {
      groups: [],
      groupCounts: [],
      items: data,
      itemToGroupMap: [],
    };
  }, [data, grouping]);

  // Track the previous first item for detecting prepends (synchronous pattern)
  // Must be declared after groupedData memo so the initializer can reference it
  const [prevFirstItem, setPrevFirstItem] = useState<T | undefined>(
    () => groupedData.items[0]
  );

  // The notification's jump back to the newest. Instant, not smooth: items
  // arriving during a smooth scroll were still held, which could stop it short
  // and leave the reader a row or two below the top with the notification back
  // up. An instant jump also makes it safe to mark the list as at the top now
  // rather than waiting for Virtuoso's throttled report, so the very next
  // arrival flows in; Virtuoso corrects the flag on its next real change.
  // Hiding the notification here covers a list too short to scroll, which
  // never reports reaching the top.
  const scrollToTop = () => {
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      align: "start",
      behavior: "auto",
    });
    setAtListTop(true);
    setShowUpdateNotification(false);
  };

  // Handle at top state changes from Virtuoso
  const handleAtTopChange = (atTop: boolean) => {
    setAtListTop(atTop);
  };

  // Get render context for an item
  const getRenderContext = (index: number): RenderContext => {
    if (!isGrouped || !groupedData.itemToGroupMap) {
      return {};
    }

    const groupIndex = groupedData.itemToGroupMap[index];
    const groupName = groupedData.groups[groupIndex];

    // Find first and last item indices in this group
    let groupStartIndex = 0;
    for (let i = 0; i < groupIndex; i++) {
      groupStartIndex += groupedData.groupCounts[i];
    }
    const groupEndIndex = groupStartIndex + groupedData.groupCounts[groupIndex];

    return {
      groupIndex,
      groupName,
      isFirstInGroup: index === groupStartIndex,
      isLastInGroup: index === groupEndIndex - 1,
    };
  };

  // Synchronous detection of prepended items during render.
  // Uses React's "store information from previous renders" pattern so that
  // firstItemIndex and data change in the SAME render cycle — eliminating
  // the one-frame flash that useEffect would cause.
  if (scrollEnabled) {
    const currentFirstItem = groupedData.items[0];

    if (!compareFirstItem(currentFirstItem, prevFirstItem)) {
      // Counted from where the old first item now sits, not from how much the
      // list grew: a capped feed that is full doesn't grow (see countNewAtTop).
      const newAtTop = countNewAtTop(
        groupedData.items,
        prevFirstItem,
        compareFirstItem
      );

      // Being at the top normally means "show me the newest", so prepends are
      // allowed to push the list down. That stops being true the moment the
      // reader opens a row: the story they are reading is then carried down the
      // viewport mid-sentence. While something is open the position is held and
      // the notification offers the jump back, exactly as when scrolled away.
      // Taking that jump lets the open row go (see VirtuosoWrapper).
      const holdPosition = !atListTop || isEngaged;

      if (newAtTop > 0 && holdPosition && !isLoadingMore) {
        setFirstItemIndex((prev) => prev - newAtTop);
        setShowUpdateNotification(true);
      }

      setPrevFirstItem(currentFirstItem);
    }
  }

  // Hide notification when user scrolls to top
  useEffect(() => {
    if (atListTop) {
      setShowUpdateNotification(false);
    }
  }, [atListTop]);

  return {
    virtuosoRef,
    firstItemIndex,
    atListTop,
    handleAtTopChange,
    showUpdateNotification,
    scrollToTop,
    groupedData,
    isGrouped,
    getRenderContext,
  };
}
